// Step Purposes for K-Beauty Routine Building
// Each step has a specific PURPOSE and concern focus
// Updated: AM 6 steps, PM 7 steps + 2 optional (2.5 and 7.5)

import type { RoutineCategory, TextureType, SkinType } from '@/types/product'
import { DB_CONCERN, type DBConcernField } from './concernIngredientMap'

// ============================================================================
// Step Purpose Definitions
// ============================================================================

export interface StepPurpose {
  purpose: string
  concernFocus: DBConcernField[] | 'DYNAMIC' // DYNAMIC = based on user's top concerns
  preferredTextures: TextureType[]
  timeSlot: 'AM' | 'PM' | 'both'
  required: boolean
  frequency?: string // e.g., '2-3x/week'
  requiresConcernScore?: number // Only include step if concern score exceeds this
  skipIfSensitive?: boolean
  activeIngredients?: boolean // Whether this step typically has actives
  maxActives?: number // Maximum active classes allowed at this step
}

export const STEP_PURPOSES: Record<RoutineCategory, StepPurpose> = {
  // ============================================================================
  // CLEANSING PHASE - Goal: Clean slate
  // ============================================================================
  cleanser: {
    purpose: 'Remove impurities and prep skin for treatment',
    concernFocus: [DB_CONCERN.CleansingPower, DB_CONCERN.PoreCare, DB_CONCERN.AcneCare],
    preferredTextures: ['foam', 'gel', 'oil', 'cream', 'water'],
    timeSlot: 'both',
    required: true,
  },

  // ============================================================================
  // PREP PHASE - Goal: Prepare skin for treatments
  // ============================================================================
  exfoliator: {
    purpose: 'Remove dead skin cells, improve texture and absorption',
    concernFocus: [DB_CONCERN.TextureSmoothing, DB_CONCERN.PoreCare, DB_CONCERN.AcneCare, DB_CONCERN.BlackheadCare, DB_CONCERN.ToneDullness],
    preferredTextures: ['gel', 'lotion', 'water'],
    timeSlot: 'PM',
    required: false,
    frequency: '2-3x/week',
    skipIfSensitive: true,
    activeIngredients: true,
  },

  'wash-off-mask': {
    purpose: 'Deep cleanse and treat specific concerns',
    concernFocus: [DB_CONCERN.PoreCare, DB_CONCERN.Hydration, DB_CONCERN.Soothing],
    preferredTextures: ['cream', 'gel', 'balm'],
    timeSlot: 'PM',
    required: false,
    frequency: '1-2x/week',
  },

  toner: {
    purpose: 'Balance pH, provide first layer of hydration, prep for absorption',
    concernFocus: [DB_CONCERN.Hydration, DB_CONCERN.PoreCare, DB_CONCERN.BarrierStrengthening],
    preferredTextures: ['water', 'lotion'],
    timeSlot: 'both',
    required: true,
  },

  essence: {
    purpose: 'Boost absorption, provide lightweight treatment layer',
    concernFocus: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening, DB_CONCERN.ToneDullness],
    preferredTextures: ['water', 'gel'],
    timeSlot: 'both',
    required: false,
  },

  // ============================================================================
  // TREATMENT PHASE - Goal: Address specific concerns
  // ============================================================================
  serum: {
    purpose: 'Concentrated treatment targeting primary skin concern',
    concernFocus: 'DYNAMIC', // Based on user's prioritized concerns
    preferredTextures: ['water', 'gel', 'lotion', 'oil'],
    timeSlot: 'both',
    required: true,
    activeIngredients: true,
    maxActives: 2, // Limit actives to prevent overload
  },

  'eye-cream': {
    purpose: 'Delicate eye area treatment for concerns specific to that zone',
    concernFocus: [DB_CONCERN.EyeCare, DB_CONCERN.AntiWrinkle, DB_CONCERN.Hydration],
    preferredTextures: ['cream', 'gel', 'balm'],
    timeSlot: 'both',
    required: true, // Now required in both AM and PM
  },

  'sheet-mask': {
    purpose: 'Intensive treatment boost, deep hydration or targeted concern',
    concernFocus: 'DYNAMIC',
    preferredTextures: ['lotion', 'gel', 'cream'],
    timeSlot: 'PM',
    required: true, // Required in PM routine
    frequency: '1-2x/week',
  },

  'spot-treatment': {
    purpose: 'Targeted treatment for localized concerns like acne or dark spots',
    concernFocus: [DB_CONCERN.AcneCare, DB_CONCERN.Pigmentation],
    preferredTextures: ['gel', 'cream', 'lotion'],
    timeSlot: 'PM',
    required: false,
    activeIngredients: true,
  },

  // ============================================================================
  // MOISTURIZE PHASE - Goal: Seal and protect
  // ============================================================================
  moisturizer: {
    purpose: 'Lock in hydration, strengthen barrier, seal treatments',
    concernFocus: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening],
    preferredTextures: ['lotion', 'gel', 'cream', 'balm'],
    timeSlot: 'both',
    required: true,
  },

  'face-oil': {
    purpose: 'Extra nourishment and moisture lock for dry/dehydrated skin',
    concernFocus: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening],
    preferredTextures: ['oil'],
    timeSlot: 'PM',
    required: false,
  },

  'sleeping-mask': {
    purpose: 'Overnight intensive hydration and treatment',
    concernFocus: [DB_CONCERN.Hydration, DB_CONCERN.BarrierStrengthening, DB_CONCERN.AntiWrinkle],
    preferredTextures: ['cream', 'gel', 'balm'],
    timeSlot: 'PM',
    required: false,
    frequency: '2-3x/week',
  },

  // ============================================================================
  // PROTECT PHASE - Goal: UV defense (AM only)
  // ============================================================================
  sunscreen: {
    purpose: 'Essential UV protection to prevent aging and damage',
    concernFocus: [DB_CONCERN.Whitening, DB_CONCERN.Pigmentation, DB_CONCERN.AntiWrinkle], // Prevents these concerns
    preferredTextures: ['lotion', 'cream', 'gel'],
    timeSlot: 'AM',
    required: true,
  },
}

// ============================================================================
// Optional Step Logic (PM Step 2.5 and 7.5)
// Based on user's skin type and concerns
// ============================================================================

export interface OptionalStepConfig {
  category: RoutineCategory
  label: string
  bestFor: {
    skinTypes: SkinType[]
    concerns: string[] // User's skin conditions that benefit
  }
  notFor: {
    skinTypes: SkinType[]
    concerns: string[] // Conditions that should avoid
  }
}

// PM Step 2.5 options - Exfoliate or Wash-Off Mask
export const PM_STEP_2_5_OPTIONS: OptionalStepConfig[] = [
  {
    category: 'exfoliator',
    label: 'Exfoliate',
    bestFor: {
      skinTypes: ['oily', 'combination'],
      concerns: ['acne', 'oiliness', 'dullness'],
    },
    notFor: {
      skinTypes: [],
      concerns: ['sensitivity', 'rosacea', 'eczema'],
    },
  },
  {
    category: 'wash-off-mask',
    label: 'Wash-Off Mask',
    bestFor: {
      skinTypes: ['oily', 'combination', 'dry'],
      concerns: ['dryness', 'oiliness'],
    },
    notFor: {
      skinTypes: [],
      concerns: [],
    },
  },
]

// PM Step 7.5 options - Face Oil, Sleeping Mask, or Spot Treatment
export const PM_STEP_7_5_OPTIONS: OptionalStepConfig[] = [
  {
    category: 'face-oil',
    label: 'Face Oil',
    bestFor: {
      skinTypes: ['dry', 'normal'],
      concerns: ['dryness'],
    },
    notFor: {
      skinTypes: ['oily'],
      concerns: ['acne', 'oiliness'],
    },
  },
  {
    category: 'sleeping-mask',
    label: 'Sleeping Mask',
    bestFor: {
      skinTypes: ['dry', 'normal', 'combination'],
      concerns: ['dryness', 'sensitivity'],
    },
    notFor: {
      skinTypes: [],
      concerns: [],
    },
  },
  {
    category: 'spot-treatment',
    label: 'Spot Treatment',
    bestFor: {
      skinTypes: ['oily', 'combination'],
      concerns: ['acne'],
    },
    notFor: {
      skinTypes: [],
      concerns: [],
    },
  },
]

/**
 * Determine which optional step to include based on user profile
 */
export function selectOptionalStep(
  options: OptionalStepConfig[],
  skinType: SkinType,
  userConcerns: string[]
): OptionalStepConfig | null {
  // Score each option based on match
  let bestOption: OptionalStepConfig | null = null
  let bestScore = -1

  for (const option of options) {
    // Check if user should avoid this option
    const shouldAvoid = option.notFor.skinTypes.includes(skinType) ||
      option.notFor.concerns.some(c => userConcerns.includes(c))

    if (shouldAvoid) continue

    // Calculate match score
    let score = 0
    if (option.bestFor.skinTypes.includes(skinType) || option.bestFor.skinTypes.includes('all')) {
      score += 2
    }
    score += option.bestFor.concerns.filter(c => userConcerns.includes(c)).length

    if (score > bestScore) {
      bestScore = score
      bestOption = option
    }
  }

  return bestScore > 0 ? bestOption : null
}

// ============================================================================
// Routine Frameworks (simplified - always use full)
// ============================================================================

export type RoutineComplexity = 'quick' | 'essential' | 'targeted'

export interface RoutineFramework {
  amSteps: RoutineCategory[]
  pmSteps: RoutineCategory[]
  description: string
  idealFor: string
}

// Only one framework now - the full K-beauty routine
export const ROUTINE_FRAMEWORKS: Record<RoutineComplexity, RoutineFramework> = {
  // Quick and essential map to targeted for backwards compatibility
  quick: {
    amSteps: ['cleanser', 'toner', 'serum', 'eye-cream', 'moisturizer', 'sunscreen'],
    pmSteps: ['cleanser', 'cleanser', 'toner', 'sheet-mask', 'serum', 'eye-cream', 'moisturizer'],
    description: 'Complete K-beauty routine',
    idealFor: 'Everyone',
  },
  essential: {
    amSteps: ['cleanser', 'toner', 'serum', 'eye-cream', 'moisturizer', 'sunscreen'],
    pmSteps: ['cleanser', 'cleanser', 'toner', 'sheet-mask', 'serum', 'eye-cream', 'moisturizer'],
    description: 'Complete K-beauty routine',
    idealFor: 'Everyone',
  },
  targeted: {
    amSteps: ['cleanser', 'toner', 'serum', 'eye-cream', 'moisturizer', 'sunscreen'],
    pmSteps: ['cleanser', 'cleanser', 'toner', 'sheet-mask', 'serum', 'eye-cream', 'moisturizer'],
    description: 'Complete K-beauty routine with optional treatment steps',
    idealFor: 'Skincare enthusiasts',
  },
}

// ============================================================================
// AM vs PM Texture Preferences
// ============================================================================

export const TIME_TEXTURE_PREFERENCES: Record<'AM' | 'PM', Record<RoutineCategory, TextureType[]>> = {
  AM: {
    cleanser: ['foam', 'gel', 'water'], // Lighter cleanse in AM
    exfoliator: [], // Not used in AM
    'wash-off-mask': [], // Not used in AM
    toner: ['water', 'lotion'],
    essence: ['water', 'gel'],
    serum: ['water', 'gel', 'lotion'], // Lightweight for layering under SPF
    'eye-cream': ['gel', 'cream'],
    'sheet-mask': [], // Not used in AM
    moisturizer: ['lotion', 'gel', 'cream'], // Lighter to go under SPF
    'sleeping-mask': [], // Not used in AM
    sunscreen: ['lotion', 'gel', 'cream'],
    'spot-treatment': [],
    'face-oil': [], // Not used in AM
  },
  PM: {
    cleanser: ['oil', 'balm', 'cream', 'foam', 'gel'], // Oil first for double cleanse
    exfoliator: ['gel', 'lotion', 'water'],
    'wash-off-mask': ['cream', 'gel', 'balm'],
    toner: ['water', 'lotion'],
    essence: ['water', 'gel'],
    serum: ['water', 'gel', 'lotion', 'oil'], // Can use richer serums at night
    'eye-cream': ['cream', 'balm', 'gel'],
    'sheet-mask': ['lotion', 'gel', 'cream'],
    moisturizer: ['cream', 'balm', 'lotion'], // Richer at night
    'sleeping-mask': ['cream', 'gel', 'balm'],
    sunscreen: [], // Not used in PM
    'spot-treatment': ['gel', 'cream'],
    'face-oil': ['oil'],
  },
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the concern focus for a step, handling DYNAMIC concerns
 */
export function getStepConcernFocus(
  category: RoutineCategory,
  userConcerns: DBConcernField[]
): DBConcernField[] {
  const stepPurpose = STEP_PURPOSES[category]

  if (stepPurpose.concernFocus === 'DYNAMIC') {
    // Return user's top 3 concerns for dynamic steps
    return userConcerns.slice(0, 3)
  }

  return stepPurpose.concernFocus
}

/**
 * Check if a step should be included based on user's concern scores
 */
export function shouldIncludeStep(
  category: RoutineCategory,
  concernScores: Record<string, number>,
  isSensitive: boolean
): boolean {
  const stepPurpose = STEP_PURPOSES[category]

  // Skip if sensitive and step should be skipped
  if (isSensitive && stepPurpose.skipIfSensitive) {
    return false
  }

  // Check if concern score requirement is met
  if (stepPurpose.requiresConcernScore) {
    const relevantConcerns = stepPurpose.concernFocus === 'DYNAMIC'
      ? Object.keys(concernScores)
      : stepPurpose.concernFocus

    const hasHighEnoughConcern = relevantConcerns.some(
      (concern) => (concernScores[concern] || 0) >= stepPurpose.requiresConcernScore!
    )

    if (!hasHighEnoughConcern) {
      return false
    }
  }

  return true
}

/**
 * Get preferred textures for a step and time
 */
export function getPreferredTextures(
  category: RoutineCategory,
  time: 'AM' | 'PM'
): TextureType[] {
  const timeTextures = TIME_TEXTURE_PREFERENCES[time][category]

  if (timeTextures.length > 0) {
    return timeTextures
  }

  // Fallback to step's general preferences
  return STEP_PURPOSES[category].preferredTextures
}

/**
 * Calculate step-purpose match score (0-1)
 */
export function calculateStepPurposeMatch(
  productConcernScores: Record<string, number>,
  stepCategory: RoutineCategory,
  userConcerns: DBConcernField[]
): number {
  const focusConcerns = getStepConcernFocus(stepCategory, userConcerns)

  if (focusConcerns.length === 0) return 0.5 // Neutral if no focus

  let totalScore = 0
  let totalWeight = 0

  for (let i = 0; i < focusConcerns.length; i++) {
    const concern = focusConcerns[i]
    const weight = 1 / (i + 1) // First concern is most important
    totalWeight += weight

    const productScore = productConcernScores[concern] || 0
    // Product scores are 0-5, normalize to 0-1
    totalScore += (productScore / 5) * weight
  }

  return totalWeight > 0 ? totalScore / totalWeight : 0.5
}
